import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CalendarCheck, ChatText, CheckCircle, Phone } from "@phosphor-icons/react";
import { SiteShell, useSite } from "@/components/site/SiteShell";
import { usePageHead } from "@/lib/siteContent";
import { API } from "@/lib/api";
import { smsHref, viewingText } from "@/lib/sms";

const today = () => new Date().toISOString().slice(0, 10);
const nextHour = () => {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  return d.toTimeString().slice(0, 5);
};

// Office-facing page from the "viewing request" alert: one tap puts the viewing on the calendar.
export default function BookViewing() {
  const site = useSite();
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ date: today(), time: nextHour(), assignee: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  usePageHead({ title: info ? `Book viewing — ${info.name}` : "Book a viewing" });

  useEffect(() => {
    fetch(`${API}/public/viewings/book/${token}`)
      .then((r) => (r.ok ? r.json() : r.json().then((e) => Promise.reject(e))))
      .then((data) => {
        setInfo(data);
        if (data.booked)
          setForm({
            date: data.booked.date,
            time: data.booked.time,
            assignee: data.booked.assignee,
            notes: data.booked.notes,
          });
      })
      .catch((e) => setError(e?.detail || "This booking link is not valid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await fetch(`${API}/public/viewings/book/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await r.json();
      if (!r.ok) throw data;
      setInfo(data);
      setDone(true);
    } catch (err) {
      setError(err?.detail || "Could not save the booking. Please try again.");
    }
    setBusy(false);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const field =
    "mt-1 w-full  border border-[color:var(--s-line)] bg-[color:var(--s-surface)] px-3 py-3 text-base text-[color:var(--s-ink)] outline-none focus:border-[color:var(--s-gold)]";
  const booked = info?.booked;
  const confirmSms = booked
    ? smsHref(info.phone, viewingText(info.name, info.shop?.name, info.item, booked.date, booked.time, info.where))
    : "";

  return (
    <SiteShell site={site} testId="book-viewing">
      <main className="px-6 pb-24 pt-6 sm:pt-8 lg:px-12" data-testid="book-viewing-page">
        <div className="mx-auto max-w-xl">
          <span className="s-micro inline-flex items-center gap-2">
            <span className="inline-block h-2 w-2 rotate-45 bg-[color:var(--s-gold)]" />
            Showroom viewing · office only
          </span>

          {error && !info && (
            <div
              className="mt-6  border border-red-300 bg-red-50 p-5 text-red-800"
              data-testid="book-viewing-error"
            >
              {error}
            </div>
          )}

          {info && (
            <>
              <h1 className="s-display mt-4 text-3xl sm:text-4xl" data-testid="book-viewing-headline">
                {info.name} wants to see {info.item || "a slab"}
              </h1>
              <p className="mt-2 text-[color:var(--s-mute)]" data-testid="book-viewing-sub">
                {info.where ? `At ${info.where}` : "In person"}
                {info.asked_at ? ` · asked ${info.asked_at.slice(0, 10)}` : ""}
              </p>

              <div className="s-card mt-8 !p-5" data-testid="book-viewing-shopper">
                <div className="s-micro">Shopper</div>
                <div className="mt-2 text-lg font-semibold">{info.name}</div>
                <div className="mt-1 text-[color:var(--s-mute)]">{info.phone}</div>
                {info.message && (
                  <p className="mt-3 whitespace-pre-wrap text-sm" data-testid="book-viewing-message">
                    “{info.message}”
                  </p>
                )}
                <div className="mt-4 flex flex-wrap gap-2">
                  <a
                    href={`tel:${info.phone.replace(/[^\d+]/g, "")}`}
                    data-testid="book-viewing-call"
                    className="s-act !w-auto"
                  >
                    <Phone size={16} weight="bold" /> Call
                  </a>
                  <a
                    href={smsHref(info.phone, `Hi ${info.name.split(/\s+/)[0]}, ${info.shop?.name || "we"} here — `)}
                    data-testid="book-viewing-text"
                    className="s-act !w-auto"
                  >
                    <ChatText size={16} weight="bold" /> Text
                  </a>
                  {info.slab_url && (
                    <a
                      href={info.slab_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="book-viewing-slab"
                      className="s-act !w-auto"
                    >
                      Open slab page
                    </a>
                  )}
                </div>
              </div>

              {done && booked ? (
                <div
                  className="mt-8  border border-emerald-300 bg-emerald-50 p-6"
                  data-testid="book-viewing-done"
                >
                  <div className="flex items-center gap-3 text-emerald-800">
                    <CheckCircle size={28} weight="fill" />
                    <div className="text-lg font-semibold">On the calendar</div>
                  </div>
                  <p className="mt-2 text-emerald-900" data-testid="book-viewing-done-text">
                    {booked.date}
                    {booked.time ? ` at ${booked.time}` : ""}
                    {booked.assignee ? ` · ${booked.assignee}` : ""}. It shows as a Viewing on the
                    office calendar.
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <a
                      href={confirmSms}
                      data-testid="book-viewing-confirm-text"
                      className="s-act s-act-gold !w-auto"
                    >
                      <ChatText size={16} weight="bold" /> Text {info.name.split(/\s+/)[0]} the time
                    </a>
                    <a href={info.calendar_url} data-testid="book-viewing-open-calendar" className="s-act !w-auto">
                      <CalendarCheck size={16} weight="bold" /> Open the calendar
                    </a>
                    <button
                      type="button"
                      data-testid="book-viewing-change"
                      onClick={() => setDone(false)}
                      className="s-ghost"
                    >
                      Change the time
                    </button>
                  </div>
                </div>
              ) : (
                <form onSubmit={submit} className="s-card mt-8 !p-6" data-testid="book-viewing-form">
                  <div className="flex items-center gap-2 text-lg font-semibold">
                    <CalendarCheck size={20} weight="bold" className="text-[color:var(--s-gold)]" />
                    {booked ? "Move the viewing" : "Put it on the calendar"}
                  </div>
                  {booked && (
                    <p className="mt-1 text-sm text-[color:var(--s-mute)]" data-testid="book-viewing-existing">
                      Already booked for {booked.date}
                      {booked.time ? ` at ${booked.time}` : ""} — saving updates that entry.
                    </p>
                  )}
                  <div className="mt-5 grid grid-cols-2 gap-3">
                    <label className="block text-sm font-semibold">
                      Date
                      <input
                        data-testid="book-viewing-date"
                        type="date"
                        required
                        value={form.date}
                        onChange={set("date")}
                        className={field}
                      />
                    </label>
                    <label className="block text-sm font-semibold">
                      Time
                      <input
                        data-testid="book-viewing-time"
                        type="time"
                        value={form.time}
                        onChange={set("time")}
                        className={field}
                      />
                    </label>
                  </div>
                  <label className="mt-4 block text-sm font-semibold">
                    Who shows them around (optional)
                    <select
                      data-testid="book-viewing-assignee"
                      value={form.assignee}
                      onChange={set("assignee")}
                      className={field}
                    >
                      <option value="">Anyone at the desk</option>
                      {(info.crew || []).map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="mt-4 block text-sm font-semibold">
                    Notes (optional)
                    <textarea
                      data-testid="book-viewing-notes"
                      rows={2}
                      value={form.notes}
                      onChange={set("notes")}
                      placeholder="Bring the 3cm sample · they also liked Taj Mahal"
                      className={field}
                    />
                  </label>
                  {error && (
                    <p className="mt-3 text-sm text-red-700" data-testid="book-viewing-form-error">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    data-testid="book-viewing-submit"
                    disabled={busy}
                    className="s-btn mt-6 w-full justify-center"
                  >
                    {busy ? "Saving…" : booked ? "Update the booking" : "Add to calendar"}
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </main>
    </SiteShell>
  );
}
